import {intersect, objectFromEntries} from '@benchling/js-utils';

import * as metrics from 'metrics/Metrics';
import {getModelManager, ManagedModelType} from 'models/ManagedModelDefinitions';
import {modelFetched} from 'models/ManagedModelsReduxActions';
import {ModelManager} from 'models/managers/ModelManager';
import {ManagedModelSelector} from 'models/reduceManagedModels';
// @ts-expect-error
// eslint-disable-next-line unused-imports/no-unused-imports
import SidepanelStore from 'nav/sidepanel/SidepanelStore';
import {pushToast} from 'nav/toasts/ToastActions';
import {ToastType} from 'nav/toasts/ToastConstants';
import {FOREVER_DURATION} from 'notifications';
import {clearProfilerTimingEvent, logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {getCurrentTime} from 'performance/getCurrentTime';
import {
  composeSidepanelSearchResultsRenderedEvent,
  composeSidepanelSearchResultsReturnedEvent,
} from 'performance/processSearchEvents';
import {getStore, Thunk} from 'redux-stores/Store';
import {
  addChildrenLoadingIds,
  addNextToken,
  removeChildrenLoadingIds,
  selectIds,
  updateRequestConsistency,
} from 'search/SearchUIReduxActions';

import {getGroupChildrenSearchParamsAndErrors} from './FilterUtil';
import {SearchUISelector} from './reduceSearchUI';
import {isBLASTFilter, isCompleteSearchResponse, SearchResults} from './SearchConstants';
import {performSearch, pollSearchStatus} from './SearchTypes';

function dispatchRefreshSelectedIds(keys: Array<string>): void {
  const selector = new SearchUISelector(getStore().getState());
  for (const searchId of keys) {
    const oldSelectedIds = selector.getSelectedIdsForSearch(searchId);
    const searchResults = getModelManager(ManagedModelType.SEARCH_RESULTS).getModel(searchId);
    if (searchResults?.ids != null && searchResults?.ids.length > 0) {
      let selectedIds: Array<string> = [];
      selectedIds = intersect([oldSelectedIds, searchResults.ids]);
      getStore().dispatch(selectIds(searchId, selectedIds));
    }
  }
}

function dispatchAddNextTokens(keys: Array<string>): void {
  for (const searchId of keys) {
    const searchResults = getModelManager(ManagedModelType.SEARCH_RESULTS).getModel(searchId);
    if (searchResults) {
      getStore().dispatch(addNextToken(searchId, searchResults.nextToken || null));
    }
  }
}

export default class SearchResultsModelManager extends ModelManager<ManagedModelType.SEARCH_RESULTS> {
  constructor() {
    super(ManagedModelType.SEARCH_RESULTS);
  }

  async doFetchMulti(keys: Array<string>): Promise<void> {
    const selector = new SearchUISelector(getStore().getState());
    const modelsByKey: {[searchId: string]: SearchResults} = {};
    const startingTime = getCurrentTime();
    const allSearchTypes = [];

    for (const searchId of keys) {
      const searchParams = selector.getParamsForSearch(searchId);
      const searchType = selector.getTypeForSearch(searchId);
      allSearchTypes.push(searchType);
      const filterErrors = selector.getFilterErrorsForSearch(searchId);
      const requestConsistency = selector.getRequestConsistencyForSearch(searchId);
      try {
        const searchResponse = await performSearch(
          searchType,
          searchParams,
          filterErrors,
          requestConsistency
        );
        // Reset to undefined (backend default) so any subsequent fetches for this searchId (e.g. group
        // child expansion) don't unnecessarily request consistency.
        if (requestConsistency) {
          getStore().dispatch(updateRequestConsistency(searchId, undefined));
        }
        let searchResults: SearchResults;
        if (isCompleteSearchResponse(searchResponse)) {
          searchResults = searchResponse;
        } else {
          searchResults = await pollSearchStatus(searchResponse.searchId, searchType);
        }
        modelsByKey[searchId] = searchResults;
        if (searchResults.timedOut) {
          const hasBLASTFilter = searchParams.filters.some((filter) => isBLASTFilter(filter));
          if (hasBLASTFilter) {
            pushToast({
              message:
                'Timeout while executing BLAST search. Please try again or contact Benchling support if the problem persists.',
              type: ToastType.DANGER,
              duration: FOREVER_DURATION,
            });
          } else {
            pushToast({
              message:
                'Only partial results were returned for chemical substructure search. Please refine your query..',
              type: ToastType.WARNING,
              duration: FOREVER_DURATION,
            });
          }
        }
        clearProfilerTimingEvent(composeSidepanelSearchResultsRenderedEvent(searchId));
        logProfilerTimingEvent(composeSidepanelSearchResultsReturnedEvent(searchId));
      } catch (e) {
        e.notificationOptions = {
          // Dedupe toasts if multiple search errors happen.
          id: `search-${searchType}`,
          duration: FOREVER_DURATION,
        };
        throw e;
      }
    }
    getStore().dispatch(modelFetched(this.modelType, modelsByKey));

    dispatchRefreshSelectedIds(keys);
    dispatchAddNextTokens(keys);

    // Add a defensive check here so we don't send misleading metrics to Datadog when no search
    // actually happened.
    if (allSearchTypes.length > 0) {
      const totalTimeMs = getCurrentTime() - startingTime;

      // If there's only one type of search, then we can use the type directly. If there are
      // more than one, for now we just categorize it as "multi" (this is expected to be rare).
      const tags = {searchType: allSearchTypes.length === 1 ? allSearchTypes[0] : 'multi'};

      // This metric right now doesn't perfectly capture the end-to-end time of searching, since some
      // work can happen before this method even gets called. As an example, clicking into a project
      // triggers several requests to get starred schemas, which are not covered by this timespan. We
      // are capturing this for now as a starting point, but may want to extend this to capture the full
      // time as needed.
      //
      // We track this directly right now instead of using Perf.ts because we don't want client-side
      // aggregations. This metric can get moved into Perf.ts when that is refactored to support
      // non-aggregation use cases.
      metrics.distribution('perf.search_results_model_manager', totalTimeMs, tags);
    }
  }

  getModel(modelKey: string): SearchResults | null {
    return new ManagedModelSelector(getStore().getState()).getModel(this.modelType, modelKey);
  }

  /**
   * Fetch the next page of child items for each group and add them to the search results model.
   *
   * This method is NOT idempotent, it simply appends the next page of children without deduplicating. For
   * this reason, we've chosen not to support pages beyond the first page when fetching multiple groups at the
   * same time. If any group has already loaded some children, request each group individually.
   *
   * Every key in groupKeys must already be present in the search results. Other keys will be ignored.
   *
   * This method is not part of the normal model manager lifecycle and must be invoked manually. This is a
   * somewhat awkward use of a model manager, because we are fetching a partial "model" and mutating model
   * state in the store. However, a more natural approach would require pretty heavy refactoring of listings,
   * so the awkward design is being accepted until we fully switch to GraphQL.
   */
  getNextGroupChildren(searchId: string, groupKeys: Array<string>, limit: number): Thunk<Promise<void>> {
    return async (dispatch, getState) => {
      if (groupKeys.length === 0) {
        return;
      }
      const groupKeysSet = new Set(groupKeys);

      const modelSelector = new ManagedModelSelector(getState());
      const currentSearchResults = modelSelector.getModel(this.modelType, searchId);
      if (!currentSearchResults || !currentSearchResults.groups) {
        throw new Error('Groups must already be present before requesting children');
      }
      const currentGroups = currentSearchResults.groups;
      if (
        groupKeys.length > 1 &&
        currentGroups.filter((g) => groupKeysSet.has(g.key)).some((g) => g.childIds.length !== 0)
      ) {
        throw new Error('When multiple groups are requested, they must not have any children loaded yet');
      }

      dispatch(addChildrenLoadingIds(searchId, groupKeys));

      const offset =
        groupKeys.length > 1 ? 0 : currentGroups.find((g) => g.key === groupKeys[0])!.childIds.length;

      const selector = new SearchUISelector(getState());
      const searchParams = selector.getParamsForSearch(searchId);
      const searchType = selector.getTypeForSearch(searchId);
      const filterErrors = selector.getFilterErrorsForSearch(searchId);
      const requestConsistency = selector.getRequestConsistencyForSearch(searchId);

      const [getChildrenSearchParams, getChildrenFilterErrors] = getGroupChildrenSearchParamsAndErrors(
        searchParams,
        groupKeys,
        filterErrors,
        offset,
        limit
      );

      const searchResponse = await performSearch(
        searchType,
        getChildrenSearchParams,
        getChildrenFilterErrors,
        requestConsistency
      );
      let searchResults: SearchResults;
      if (isCompleteSearchResponse(searchResponse)) {
        searchResults = searchResponse;
      } else {
        searchResults = await pollSearchStatus(searchResponse.searchId, searchType);
      }

      const groupResultsByKey = objectFromEntries(searchResults.groups?.map((g) => [g.key, g]) ?? []);
      const newGroups = currentSearchResults.groups.map((group) => {
        if (groupResultsByKey.hasOwnProperty(group.key)) {
          return {
            ...group,
            childIds: group.childIds.concat(groupResultsByKey[group.key].childIds),
            childItems: group.childItems.concat(groupResultsByKey[group.key].childItems),
          };
        } else {
          return group;
        }
      });

      dispatch(removeChildrenLoadingIds(searchId, groupKeys));

      dispatch(
        modelFetched(this.modelType, {
          [searchId]: {
            ...currentSearchResults,
            groups: newGroups,
          },
        })
      );
    };
  }
}
